// src/components/operationsAssociate/InventoryTable.jsx

import React, { useState, useMemo, useEffect } from "react";
import moment from "moment";
import { 
  Eye, Edit3, Plus, Minus, Download, Search, 
  FileText, FileSpreadsheet, File as PdfIcon, ChevronDown,
  Package, MapPin, Tag, Truck
} from "lucide-react";
import { motion, AnimatePresence } from "framer-motion";
import * as XLSX from "xlsx";
import { saveAs } from "file-saver";
import jsPDF from "jspdf";
import "jspdf-autotable";

// Import the new modal components
import IssueItemModal from "./IssueItemModal";
import ViewItemHistoryModal from "./ViewItemHistoryModal";
import ReceiveItemModal from "./ReceiveItemModal";
import EditItemModal from "./EditItemModal";

const API_URL = "https://ims.holdthechild.net";

const InventoryTable = ({ items, onUpdate }) => {
    const [search, setSearch] = useState("");
    const [currentPage, setCurrentPage] = useState(1);
    const itemsPerPage = 20;

    // State for managing modals and the selected item
    const [isIssueModalOpen, setIsIssueModalOpen] = useState(false);
    const [isReceiveModalOpen, setIsReceiveModalOpen] = useState(false);
    const [isEditModalOpen, setIsEditModalOpen] = useState(false);
    const [isHistoryModalOpen, setIsHistoryModalOpen] = useState(false);
    const [selectedItem, setSelectedItem] = useState(null);
    const [exportDropdownOpen, setExportDropdownOpen] = useState(false);

    const filteredItems = useMemo(() => {
        const q = search.toLowerCase();
        let data = [...items];
        // Sort by most recently added item
        data.sort((a, b) => new Date(b.createdAt) - new Date(a.createdAt));

        if (!q) return data;

        // MODIFIED: Added vendor name to the search criteria
        return data.filter((item) =>
            `${item.name} ${item.sku} ${item.category} ${item.location} ${item.vendor?.name || ''}`.toLowerCase().includes(q)
        );
    }, [items, search]);

    useEffect(() => {
        setCurrentPage(1);
    }, [search, items]);

    const paginatedItems = useMemo(() => {
        const start = (currentPage - 1) * itemsPerPage;
        return filteredItems.slice(start, start + itemsPerPage);
    }, [filteredItems, currentPage]);

    const totalPages = Math.ceil(filteredItems.length / itemsPerPage);

    // Determines the color of the "In Stock" badge based on stock level
    const getStockStatusColor = (item) => {
        if (item.quantity <= item.minThreshold) return 'text-red-600 bg-red-50 border-red-100';
        return 'text-emerald-600 bg-emerald-50 border-emerald-100';
    };

    // --- Export functions for data table ---
    const mapItemToExportRow = (item) => ({
        "Item Name": item.name,
        "SKU": item.sku,
        "Category": item.category,
        "Vendor": item.vendor?.name || "N/A", // ADDED: Include vendor name in export
        "In Stock": item.quantity,
        "Reserved": item.reserved, // Renamed for clarity
        "Location": item.location,
        "Created At": moment(item.createdAt).format("YYYY-MM-DD"),
    });

    const exportToExcel = () => {
        const exportData = filteredItems.map(mapItemToExportRow);
        const worksheet = XLSX.utils.json_to_sheet(exportData);
        const workbook = XLSX.utils.book_new();
        XLSX.utils.book_append_sheet(workbook, worksheet, "Inventory");
        const excelBuffer = XLSX.write(workbook, { bookType: "xlsx", type: "array" });
        saveAs(new Blob([excelBuffer]), "inventory_export.xlsx");
        setExportDropdownOpen(false);
    };

    const exportToPDF = () => {
        const exportData = filteredItems.map(mapItemToExportRow);
        const doc = new jsPDF();
        const columns = Object.keys(exportData[0]);
        doc.autoTable({
            head: [columns],
            body: exportData.map(row => Object.values(row)),
            startY: 20
        });
        doc.save("inventory_export.pdf");
        setExportDropdownOpen(false);
    };

    // --- Modal Handlers ---
    const handleOpenIssueModal = (item) => {
        setSelectedItem(item);
        setIsIssueModalOpen(true);
    };

    const handleCloseIssueModal = () => {
        setIsIssueModalOpen(false);
        setSelectedItem(null);
        onUpdate(); // Refresh the table after the action
    };

    const handleOpenReceiveModal = (item) => {
        setSelectedItem(item);
        setIsReceiveModalOpen(true);
    };

    const handleCloseReceiveModal = () => {
        setIsReceiveModalOpen(false);
        setSelectedItem(null);
        onUpdate();
    };

    const handleOpenEditModal = (item) => {
        setSelectedItem(item);
        setIsEditModalOpen(true);
    };

    const handleCloseEditModal = () => {
        setIsEditModalOpen(false);
        setSelectedItem(null);
        onUpdate();
    };

    const handleOpenHistoryModal = (item) => {
        setSelectedItem(item);
        setIsHistoryModalOpen(true);
    };

    const handleCloseHistoryModal = () => {
        setIsHistoryModalOpen(false);
        setSelectedItem(null);
    };

    return (
        <div className="space-y-6">
            <div className="flex flex-col md:flex-row justify-between items-center gap-4 bg-gray-50/50 p-4 rounded-[2rem] border border-gray-100">
                <div className="relative w-full md:max-w-md group">
                    <Search className="absolute left-4 top-1/2 -translate-y-1/2 text-gray-400 group-focus-within:text-orange-600 transition-colors" size={16} />
                    <input
                        type="search"
                        placeholder="Search items by name, SKU, or category..."
                        value={search}
                        onChange={(e) => setSearch(e.target.value)}
                        className="w-full bg-white border border-gray-200 rounded-2xl py-3.5 pl-12 pr-4 text-[11px] font-black uppercase tracking-widest focus:ring-4 focus:ring-orange-100 focus:border-orange-600 transition-all outline-none"
                    />
                </div>
                
                <div className="relative">
                    <button 
                        onClick={() => setExportDropdownOpen(!exportDropdownOpen)}
                        className="bg-white border-2 border-gray-900 text-gray-900 px-6 py-3 rounded-2xl text-[11px] font-black uppercase tracking-widest hover:bg-gray-900 hover:text-white transition-all flex items-center gap-3 active:scale-95 shadow-lg shadow-gray-100"
                    >
                        <Download size={14} /> Export Options <ChevronDown size={14} className={`transition-transform duration-300 ${exportDropdownOpen ? 'rotate-180' : ''}`} />
                    </button>
                    
                    <AnimatePresence>
                        {exportDropdownOpen && (
                            <motion.div 
                                initial={{ opacity: 0, y: 10, scale: 0.95 }}
                                animate={{ opacity: 1, y: 0, scale: 1 }}
                                exit={{ opacity: 0, y: 10, scale: 0.95 }}
                                className="absolute right-0 mt-3 w-56 bg-white rounded-3xl shadow-2xl border border-gray-100 p-2 z-50 overflow-hidden"
                            >
                                <button onClick={exportToExcel} className="flex items-center gap-3 w-full p-4 hover:bg-emerald-50 text-emerald-700 rounded-2xl transition-colors font-black text-[10px] uppercase tracking-widest">
                                    <FileSpreadsheet size={16} /> Excel Sheet
                                </button>
                                <button onClick={exportToPDF} className="flex items-center gap-3 w-full p-4 hover:bg-red-50 text-red-700 rounded-2xl transition-colors font-black text-[10px] uppercase tracking-widest">
                                    <PdfIcon size={16} /> PDF Document
                                </button>
                            </motion.div>
                        )}
                    </AnimatePresence>
                </div>
            </div>

            <div className="overflow-x-auto">
                <table className="w-full text-left">
                    <thead>
                        <tr className="bg-gray-50/50">
                            <th className="px-8 py-6 text-[11px] font-black text-gray-400 uppercase tracking-widest">Product / Identifier</th>
                            <th className="px-8 py-6 text-[11px] font-black text-gray-400 uppercase tracking-widest">Category</th>
                            <th className="px-8 py-6 text-[11px] font-black text-gray-400 uppercase tracking-widest">Provenance</th>
                            <th className="px-8 py-6 text-[11px] font-black text-gray-400 uppercase tracking-widest">Stock Level</th>
                            <th className="px-8 py-6 text-[11px] font-black text-gray-400 uppercase tracking-widest">Logistics</th>
                            <th className="px-8 py-6 text-[11px] font-black text-gray-400 uppercase tracking-widest text-right">Operations</th>
                        </tr>
                    </thead>
                    <tbody className="divide-y divide-gray-100/50">
                        {paginatedItems.length === 0 ? (
                            <tr>
                                <td colSpan="6" className="px-8 py-24 text-center">
                                    <div className="flex flex-col items-center opacity-30">
                                        <Package size={48} className="text-gray-300 mb-4" />
                                        <p className="text-[11px] font-black uppercase tracking-[0.3em]">No SKUs detected in current buffer</p>
                                    </div>
                                </td>
                            </tr>
                        ) : (
                            paginatedItems.map((item, idx) => (
                                <motion.tr 
                                    key={item._id}
                                    initial={{ opacity: 0, y: 10 }}
                                    animate={{ opacity: 1, y: 0 }}
                                    transition={{ delay: idx * 0.03 }}
                                    className="group hover:bg-gray-50/50 transition-colors"
                                >
                                    <td className="px-8 py-6">
                                        <div className="flex flex-col">
                                            <span className="text-sm font-black text-gray-800 uppercase leading-none tracking-tight">{item.name}</span>
                                            <span className="text-[10px] font-black text-orange-600 mt-2 uppercase tracking-widest flex items-center gap-1.5 opacity-70 group-hover:opacity-100 transition-opacity">
                                                <Tag size={10} /> {item.sku}
                                            </span>
                                        </div>
                                    </td>
                                    <td className="px-8 py-6">
                                        <div className="flex items-center gap-2">
                                            <div className="w-2 h-2 rounded-full bg-blue-600 shadow-sm" />
                                            <span className="text-[11px] font-black text-gray-600 uppercase tracking-tight">{item.category}</span>
                                        </div>
                                    </td>
                                    <td className="px-8 py-6">
                                        <div className="flex items-center gap-3">
                                            <div className="h-8 w-8 rounded-full bg-orange-100 flex items-center justify-center font-black text-[10px] text-orange-600 border border-orange-200">
                                                {item.vendor?.name?.[0] || 'V'}
                                            </div>
                                            <span className="text-[11px] font-black text-gray-700 uppercase tracking-tight">{item.vendor?.name || "N/A"}</span>
                                        </div>
                                    </td>
                                    <td className="px-8 py-6">
                                        <div className={`inline-flex items-center gap-2 px-4 py-2 rounded-xl border ${getStockStatusColor(item)}`}>
                                            <span className="text-sm font-black tracking-tight">{item.quantity}</span>
                                            <span className="text-[9px] font-black uppercase tracking-widest opacity-60">Units</span>
                                        </div>
                                    </td>
                                    <td className="px-8 py-6">
                                        <div className="flex items-center gap-1.5 text-gray-500">
                                            <MapPin size={12} className="text-gray-400" />
                                            <span className="text-[10px] font-black uppercase tracking-widest">{item.location || "Central"}</span>
                                        </div>
                                    </td>
                                    <td className="px-8 py-6 text-right">
                                        <div className="flex justify-end gap-2">
                                            <button 
                                                onClick={() => handleOpenReceiveModal(item)} 
                                                className="p-3 bg-emerald-50 text-emerald-600 rounded-2xl hover:bg-emerald-600 hover:text-white transition-all transform hover:-translate-y-1 shadow-sm active:scale-90"
                                                title="Receive Stock"
                                            >
                                                <Plus size={16} />
                                            </button>
                                            <button 
                                                onClick={() => handleOpenIssueModal(item)} 
                                                className="p-3 bg-red-50 text-red-600 rounded-2xl hover:bg-red-600 hover:text-white transition-all transform hover:-translate-y-1 shadow-sm active:scale-90"
                                                title="Issue Stock"
                                            >
                                                <Minus size={16} />
                                            </button>
                                            <button 
                                                onClick={() => handleOpenEditModal(item)} 
                                                className="p-3 bg-orange-50 text-orange-600 rounded-2xl hover:bg-orange-600 hover:text-white transition-all transform hover:-translate-y-1 shadow-sm active:scale-90"
                                                title="Modify SKU"
                                            >
                                                <Edit3 size={16} />
                                            </button>
                                            <button 
                                                onClick={() => handleOpenHistoryModal(item)} 
                                                className="p-3 bg-gray-900 text-white rounded-2xl hover:bg-gray-800 transition-all transform hover:-translate-y-1 shadow-sm active:scale-90"
                                                title="Audit Trail"
                                            >
                                                <Eye size={16} />
                                            </button>
                                        </div>
                                    </td>
                                </motion.tr>
                            ))
                        )}
                    </tbody>
                </table>
            </div>

            {/* Pagination controls would go here */}

            {/* Modals for various actions, rendered conditionally */}
            <AnimatePresence>
                {(isIssueModalOpen || isReceiveModalOpen || isEditModalOpen || isHistoryModalOpen) && (
                    <div className="fixed inset-0 z-[100] flex items-center justify-center p-4">
                        <motion.div 
                            initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }}
                            onClick={() => {
                                setIsIssueModalOpen(false); setIsReceiveModalOpen(false);
                                setIsEditModalOpen(false); setIsHistoryModalOpen(false);
                            }}
                            className="absolute inset-0 bg-gray-900/60 backdrop-blur-sm" 
                        />
                        <motion.div 
                            initial={{ scale: 0.95, opacity: 1, y: 20 }} animate={{ scale: 1, opacity: 1, y: 0 }} exit={{ scale: 0.95, opacity: 0, y: 20 }}
                            className="relative bg-white rounded-[3rem] shadow-2xl w-full max-w-2xl max-h-[90vh] overflow-y-auto border border-gray-100"
                        >
                            {isIssueModalOpen && selectedItem && <IssueItemModal closeModal={handleCloseIssueModal} onIssue={onUpdate} item={selectedItem} />}
                            {isReceiveModalOpen && selectedItem && <ReceiveItemModal closeModal={handleCloseReceiveModal} onReceive={onUpdate} item={selectedItem} />}
                            {isEditModalOpen && selectedItem && <EditItemModal closeModal={handleCloseEditModal} onUpdate={onUpdate} item={selectedItem} />}
                            {isHistoryModalOpen && selectedItem && <ViewItemHistoryModal closeModal={handleCloseHistoryModal} item={selectedItem} />}
                        </motion.div>
                    </div>
                )}
            </AnimatePresence>
        </div>
    );
};

export default InventoryTable;
